import { ICartData, ICartItem, INewCreditCardEvent } from '../interfaces';
import { getQueryString } from '../../../../domain/url-provider';
import { ProductDepartmentEnum } from '../../../products/enums/product-department-enum';
import { format } from 'date-fns';
import { LastPaymentOption } from '../enums/last-payment-option-enum';
import { PaymentTabsEnum } from '../enums/payment-tabs-enum';
import { CreditCardServiceEnum } from '../enums/credit-card-service-enum';
import { getFromStorage } from '../../../../DAL/storage-provider';

export const SELECTED_PAYMENT_TAB_STORAGE_KEY = 'selected-payment-tab-key';
export const FIELD_DEBOUNCE_DURATION = 500;

/**
 * Remove 'productid' from query string (if exists),
 * otherwise, the same product could be added multiple times.
 * The main 'productid' logic occurs on server side.
 * For in-app, remove all the relevant query strings.
 * For cart reconstruction, like /cart?sku477=2&sku355=3, remove all
 * keys starting with 'sku'
 */
export const removeQueryStringsOnLoad = () => {
    const qs = getQueryString(window.location.search, false);
    const url = new URL(window.location.href.toLowerCase());
    let changed = false;

    const set = new Set(['productid', 'sessiontoken', 'userguid', 'purchasetoken', 'appcode']);

    for(let key of Object.keys(qs)){

        // URLSearchParams keys are case sensitive !!!
        const formattedKey = key.toLowerCase().trim();

        if(set.has(formattedKey) || formattedKey.startsWith('sku')){
            url.searchParams.delete(key);
            changed = true;
        }
    }

    if(changed){
        window.history.replaceState({}, '', url.toString());
    }
};

/**
 * https://wavesaudio.atlassian.net/browse/DSWEB-3288
 */
export const isCartStep3PPCC50AbTest = (cartData: ICartData) => {

    const isNoLastPaymentAndUs =
        !cartData.lastPaymentOption &&
        !!cartData.maxMindAddress &&
        cartData.maxMindAddress.countryCode === 'US';

    if(!isNoLastPaymentAndUs) return false;

    const isZeroPurchase = cartData.priceData.totalPrice < 1 && !checkIsSubscription(cartData);
    if(isZeroPurchase) return false;

    if(
        cartData.creditCardService === CreditCardServiceEnum.FirstData &&
        cartData.priceData.totalPrice <= 21.15) return false;

    return true;
};

/**
 * https://wavesaudio.atlassian.net/browse/DSWEB-3278
 */
export const getInitialSelectedPaymentTab = (cartData: ICartData) : PaymentTabsEnum => {

    let restored = getFromStorage('session-storage', SELECTED_PAYMENT_TAB_STORAGE_KEY);
    if(restored !== null && (
        Number(restored) === PaymentTabsEnum.CreditCard ||
        Number(restored) === PaymentTabsEnum.PayPal ||
        Number(restored) === PaymentTabsEnum.AliPay
    )){
        return Number(restored) as PaymentTabsEnum;
    }

    if(isCartStep3PPCC50AbTest(cartData)) {
        return PaymentTabsEnum.PayPal;
    }

    switch (cartData.lastPaymentOption) {
        case LastPaymentOption.AliPay: {
            return PaymentTabsEnum.AliPay;
        }

        case LastPaymentOption.PayPal: {
            return PaymentTabsEnum.PayPal;
        }
    }

    return PaymentTabsEnum.CreditCard;
};

// ------------------- ZOOZ -------------------------------

/**
 * get zooz error from their response
 */
export const getZoozSecureFieldsError = (evt: INewCreditCardEvent) => {
    if(!evt || !evt.error) return '';

    const errors = Object.values(evt.error).filter(err => !!err);
    if(errors.length <= 0) return '';

    return errors.join('. ');
};

export const checkIsSubscription = (cartData: ICartData) => {
    const active = cartData.cartItems.filter(cartItem => cartItem.isActive);

    return active.length === 1 &&
        (active[0].skuDepartmentID === ProductDepartmentEnum.Subscription ||
         active[0].skuDepartmentID === ProductDepartmentEnum.SubscriptionDynamic);
};

export const getWupDate = (cartItem: ICartItem) => {
    if(!cartItem.targetWUPEndDate) return '';

    try{
        return format(new Date(cartItem.targetWUPEndDate),'MMM dd, yyyy')
    }
    catch(ex){
        console.log(ex, `"${ cartItem.targetWUPEndDate }" is wrong.`);
    }

    return '';
};

export const getProductsCount = (cartItems: ICartItem[]) => {
    let count = 0;
    for(const item of cartItems) {
        if(!item.isActive) continue;
        count += item.skuUnits;
    }
    return count;
};
